/**
 * This store was created to replace the use of query-params-as-store which this project had
 * outgrown. It holds the view state of the chat page.
 */

import { defineStore } from 'pinia';
import qs from 'query-string';
import { LocationQuery } from 'vue-router';
import { ChatStatus, ChatTreatment } from '@/types/Api';
import { FiltersFlatObject } from '@/types/Ui/RtmChatFilters';

import { usePreferencesStore } from './PreferencesStore';

export const useRtmFiltersStore = defineStore(
  'RtmFiltersStore',
  () => {
    const assignee = ref<string | undefined>(); // id
    const callDue = ref<boolean | undefined>();
    const callOnly = ref<boolean | undefined>();
    const clinic = ref<string | undefined>(); // id
    const contacted = ref<boolean | undefined>();
    const declinedReason = ref<string | undefined>();
    const group = ref<string | undefined>(); // id
    const missedCall = ref<boolean | undefined>();
    const outreachCallDue = ref<boolean | undefined>();
    const spanishLanguage = ref<boolean | undefined>();
    const needsReview = ref<boolean | undefined>();
    const starred = ref<boolean | undefined>();
    const status = ref<ChatStatus | undefined>();
    const textDue = ref<boolean | undefined>();
    const checkInDue = ref<boolean | undefined>();
    const treatment = ref<ChatTreatment>('RTM');
    const unreplied = ref<boolean | undefined>();
    const unresponsive = ref<boolean | undefined>();
    const minCallTime = ref<number | undefined | string>();
    const maxCallTime = ref<number | undefined | string>();
    const minInteractions = ref<number | undefined | string>();
    const maxInteractions = ref<number | undefined | string>();
    const carePlanStatus = ref<string | undefined>();

    const { userId } = storeToRefs(usePreferencesStore());
    const filters = {
      assignee,
      callDue,
      callOnly,
      clinic,
      contacted,
      declinedReason,
      group,
      missedCall,
      outreachCallDue,
      spanishLanguage,
      needsReview,
      starred,
      status,
      textDue,
      checkInDue,
      treatment,
      unreplied,
      unresponsive,
      minCallTime,
      maxCallTime,
      minInteractions,
      maxInteractions,
      carePlanStatus,
    };

    // if the user changes, clear the clinic and assignee filters
    watch(
      userId,
      (newUserId) => {
        clinic.value = undefined;
        assignee.value = undefined;
      },
      { immediate: true }
    );

    watch(
      status,
      (status) => {
        if (status !== 'PENDING') {
          contacted.value = undefined;
        }
      },
      { immediate: true }
    );

    const flatObject = computed(() => {
      const result: LocationQuery = {};
      let k: keyof typeof filters;
      for (k in filters) {
        if (filters[k].value !== undefined && filters[k].value !== null) {
          if (
            [
              'minCallTime',
              'maxCallTime',
              'minInteractions',
              'maxInteractions',
            ].includes(k)
          ) {
            // Prevent sending empty string values for number fields
            if (filters[k].value !== '') {
              result[k] = filters[k].value;
            }
          } else {
            result[k] = filters[k].value;
          }
        }
      }
      return result;
    });

    const queryString = computed(() => {
      return qs.stringify(flatObject.value);
    });

    /////////////
    // Folders //
    /////////////

    // "folders" are mutually exclusive filters; chat page can only represent one of these
    // filters at a time, so we ensure that is the case. The "status" filter is a special case
    // where it may have one of many values, or be undefined and be considered the current key
    // (see below)
    const folders = {
      status,
      missedCall,
      unreplied,
      callDue,
      outreachCallDue,
      textDue,
      starred,
      checkInDue,
    };
    // create arrays of the folder keys and the folder values
    const { folderKeys, folderValues } = Object.keys(folders).reduce(
      (acc, key) => {
        acc.folderKeys.push(key);
        acc.folderValues.push(folders[key]);
        return acc;
      },
      {
        folderKeys: [] as string[],
        folderValues: [] as Ref<boolean | ChatStatus>[],
      }
    );
    const activeFolderKey = computed(() => {
      let fk: keyof typeof folders;
      for (fk in folders) {
        if (folders[fk].value) {
          return fk;
        }
      }
      return 'status';
    });
    const activeFolderValue = computed(() => {
      return folders[activeFolderKey.value].value;
    });

    watch(folderValues, (newValues, oldValues) => {
      // find which index is changing
      const changedIndex = newValues.findIndex(
        (v, i) => !!v && v !== oldValues[i]
      );
      if (changedIndex >= 0) {
        for (let i = 0; i < folderValues.length; i++) {
          if (i !== changedIndex || !folderValues[i].value) {
            folderValues[i].value = undefined;
          }
        }
      }
    });

    // status is the only key that is "on" when it is undefined; however the watcher ignores undefined
    // (it only cares about which key is getting turned on, and undefined otherwise means "off") so
    // this function is used to reset, i.e. no folder filters on which means "all patients"
    const resetActiveFolderKey = () => {
      folders[activeFolderKey.value].value = undefined;
    };

    const activeFolderName = computed(() => {
      switch (activeFolderKey.value) {
        case 'status':
          switch (activeFolderValue.value) {
            case undefined:
              return 'All Patients';
            case 'PENDING':
              return contacted.value ? 'Onboarding' : 'Uncontacted';
            case 'ENROLLED':
              return 'Enrolled';
            case 'DECLINED':
              return 'Declined';
          }
          break;
        case 'starred':
          return 'Starred';
        case 'missedCall':
          return 'Missed Call';
        case 'unreplied':
          return 'Unread';
        case 'callDue':
          return 'Monthly Call';
        case 'outreachCallDue':
          return 'Outreach Call';
        case 'textDue':
          return 'Outreach Text';
        case 'checkInDue':
          return 'Check-in Due';
      }
      return '';
    });
    return {
      // filter values
      assignee,
      callDue,
      callOnly,
      clinic,
      contacted,
      declinedReason,
      group,
      missedCall,
      outreachCallDue,
      spanishLanguage,
      needsReview,
      starred,
      status,
      textDue,
      checkInDue,
      treatment,
      unreplied,
      unresponsive,
      minCallTime,
      maxCallTime,
      minInteractions,
      maxInteractions,
      carePlanStatus,

      // query-like representation of all filters
      flatObject,
      queryString,

      // mutually exclusive filters that behave like folders
      activeFolderKey,
      activeFolderValue,
      resetActiveFolderKey, // for setting folder to "all patients"
      activeFolderName,
    };
  },
  {
    // persist is property for pinia persist plugin
    persist: true, // eslint-disable-line
  }
);
